import styled from 'styled-components';

const colors = {
    surface: '#2d4059',
    surfaceAlt: '#26384f',
    border: 'rgba(255, 255, 255, 0.12)',
    text: '#f7f9fc',
    muted: '#afbdd0',
    red: '#e30710',
    blue: '#19a9d5',
};

export const FormPage = styled.main`
    box-sizing: border-box;
    width: calc(100% - 280px);
    min-height: calc(100vh - 55px);
    padding: 28px;
    color: ${colors.text};

    @media (max-width: 900px) {
        width: 100%;
        padding: 18px;
    }
`;

export const FormHeader = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    max-width: 1050px;
    margin-bottom: 20px;
`;

export const HeaderCopy = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

export const FormTitle = styled.h1`
    margin: 0;
    font-size: 27px;
    line-height: 1.2;
`;

export const FormSubtitle = styled.p`
    margin: 6px 0 0;
    color: ${colors.muted};
    font-size: 14px;
`;

export const BackButton = styled.button`
    width: 38px;
    height: 38px;
    flex: 0 0 auto;
    border: 1px solid ${colors.border};
    border-radius: 8px;
    background: ${colors.surface};
    color: ${colors.text};
    cursor: pointer;

    &:hover { background: #354b67; }
    &:focus-visible { outline: 2px solid ${colors.blue}; outline-offset: 2px; }
`;

export const FormCard = styled.form`
    box-sizing: border-box;
    width: 100%;
    max-width: 1050px;
    overflow: hidden;
    border: 1px solid ${colors.border};
    border-radius: 12px;
    background: ${colors.surface};
    box-shadow: 0 12px 30px rgba(8, 15, 25, 0.2);
`;

export const FormSection = styled.section`
    padding: 22px;
    border-top: 1px solid ${colors.border};

    &:first-child { border-top: 0; }
`;

export const FormSectionTitle = styled.h2`
    margin: 0 0 5px;
    font-size: 17px;
`;

export const FormSectionDescription = styled.p`
    margin: 0 0 18px;
    color: ${colors.muted};
    font-size: 13px;
`;

export const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    @media (max-width: 680px) {
        grid-template-columns: 1fr;
    }
`;

export const FormField = styled.label`
    display: flex;
    flex-direction: column;
    grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
    gap: 7px;
    color: ${colors.muted};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;

    .react-datepicker-wrapper,
    .react-datepicker__input-container {
        display: block;
        width: 100%;
    }

    .react-datepicker__input-container input {
        box-sizing: border-box;
        width: 100%;
        height: 42px;
        padding: 0 12px;
        border: 1px solid rgba(255, 255, 255, 0.16);
        border-radius: 8px;
        background: #f8fafc;
        color: #202b3a;
        font: inherit;
        font-size: 14px;
    }
`;

const controlStyles = `
    box-sizing: border-box;
    width: 100%;
    height: 42px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    background: #f8fafc;
    color: #202b3a;
    font: inherit;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: ${colors.blue};
        box-shadow: 0 0 0 3px rgba(25, 169, 213, 0.18);
    }

    &:disabled {
        background: #dfe5ec;
        color: #687386;
        cursor: not-allowed;
    }
`;

export const FormInput = styled.input`${controlStyles}`;
export const FormSelect = styled.select`${controlStyles}`;
export const FormTextarea = styled.textarea`
    ${controlStyles}
    min-height: 92px;
    padding-top: 11px;
    resize: vertical;
`;

export const CheckboxField = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 42px;
    color: ${colors.text};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    input {
        width: 18px;
        height: 18px;
        accent-color: ${colors.red};
    }
`;

export const LockedNotice = styled.div`
    margin-bottom: 16px;
    padding: 11px 13px;
    border: 1px solid rgba(232, 186, 37, 0.35);
    border-radius: 8px;
    background: rgba(232, 186, 37, 0.1);
    color: #f5cf58;
    font-size: 13px;
    line-height: 1.45;
`;

export const FormActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 18px 22px;
    border-top: 1px solid ${colors.border};
    background: ${colors.surfaceAlt};
`;

export const FormButton = styled.button`
    min-height: 40px;
    padding: 0 16px;
    border: 1px solid ${props => props.$primary ? colors.red : colors.border};
    border-radius: 8px;
    background: ${props => props.$primary ? colors.red : 'transparent'};
    color: ${colors.text};
    font: inherit;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;

    &:hover { background: ${props => props.$primary ? '#c90008' : 'rgba(255, 255, 255, 0.08)'}; }
    &:disabled { opacity: 0.6; cursor: wait; }
`;

export const ErrorMessage = styled.div`
    max-width: 1050px;
    margin-bottom: 16px;
    padding: 12px 14px;
    border: 1px solid rgba(227, 7, 16, 0.4);
    border-radius: 8px;
    background: rgba(227, 7, 16, 0.12);
    color: #ff9ca1;
    font-size: 14px;
`;

export const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: ${props => props.$show ? 'flex' : 'none'};
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(8, 15, 25, 0.78);
    backdrop-filter: blur(3px);
`;

export const ModalCard = styled.div`
    display: flex;
    flex-direction: column;
    width: min(980px, 100%);
    max-height: 92vh;
    overflow: hidden;
    border: 1px solid ${colors.border};
    border-radius: 14px;
    background: ${colors.surface};
    color: ${colors.text};
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.45);
`;

export const ModalHeader = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding: 20px 22px;
    border-bottom: 1px solid ${colors.border};
    background: ${colors.surfaceAlt};
`;

export const ModalTitle = styled.h2`
    margin: 0;
    font-size: 21px;
`;

export const ModalSubtitle = styled.p`
    margin: 5px 0 0;
    color: ${colors.muted};
    font-size: 13px;
`;

export const CloseButton = styled.button`
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    border: 1px solid ${colors.border};
    border-radius: 8px;
    background: transparent;
    color: ${colors.text};
    cursor: pointer;

    &:hover { background: rgba(255, 255, 255, 0.08); }
`;

export const ModalBody = styled.div`
    overflow-y: auto;
`;

export const ModalForm = styled.form`
    width: 100%;
`;
